import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import { reconnectAccount, type Account } from '@/features/accounts/accounts.api';
import { accountsQueryKey } from '@/features/accounts/useAccounts';

/**
 * Extract the human-readable failure message for a reconnect error.
 *
 * The Hub answers with a specific `detail` string (409 "account is banned;
 * use 重新扫码 ...", 503 "gateway is unavailable for reconnect", 404 "account
 * not found") — surface it verbatim; anything else (network blip) falls back
 * to a generic Chinese message.
 */
function reconnectErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }
  return '重连失败，请重试';
}

/**
 * Mutation over `POST /api/v1/accounts/{id}/reconnect` — finalize a Baileys
 * 515 / cleanly reconnect ONE account WITHOUT restarting the gateway (no
 * account blink, no ~20s hub inbound-loss window) and WITHOUT wiping
 * credentials.
 *
 * Mirrors `useRescanAccount`: deliberately NOT optimistic. The endpoint runs
 * both gateway RPCs (channels.stop then channels.start) before returning the
 * CURRENT DB row — the status is NOT forced (it may still be `qr_required` /
 * `connecting`); the natural progression to `connected` lands via the 30s
 * `useAccounts` poll and the WS `account.status` projection. On success we
 * write the returned row into the `['accounts']` cache in place (so any
 * subsequent status change from the server merges cleanly) and toast a
 * confirmation. On error the backend `detail` surfaces via a sonner toast.
 *
 * Unlike `useRescanAccount` this reconnect PRESERVES credentials — it is the
 * surgical alternative to a gateway restart for a just-scanned account stuck
 * at 515, NOT the destructive re-pair (that is `useRescanAccount`).
 */
export function useReconnectAccount(): UseMutationResult<Account, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation<Account, unknown, string>({
    mutationFn: (accountId) => reconnectAccount(accountId),

    onSuccess: (updated) => {
      queryClient.setQueryData<Account[]>(accountsQueryKey, (rows) =>
        rows?.map((row) => (row.id === updated.id ? updated : row)),
      );
      toast.success('已触发重连，请稍候');
    },

    onError: (error) => {
      toast.error(reconnectErrorMessage(error));
    },
  });
}
